import {
  CheckSquare,
  Building2,
  Palette,
  Users,
  UserCog,
  BadgeCheck,
  LayoutDashboard,
  Armchair,
  Megaphone,
} from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { IoChevronForward } from "react-icons/io5";
import { RiMenuFoldLine, RiMenuUnfoldLine } from "react-icons/ri";
import { useEffect, useRef } from "react";
import { Z_INDEX } from "../../utils/constants";

const SuperAdminSidebar = ({
  isSidebarOpen,
  onClose,
  isSidebarExpanded,
  setIsSidebarExpanded,
}) => {
  const location = useLocation();
  const sidebarRef = useRef(null);

  const isOpen = isSidebarExpanded || isSidebarOpen;

  const menuItems = [
    // 1. Dashboard
    {
      path: "/super-admin",
      label: "Dashboard",
      icon: <LayoutDashboard size={22} strokeWidth={1.75} />,
    },

    // 2. Approvals
    {
      path: "/super-admin/approvals",
      label: "Approval Requests",
      icon: <CheckSquare size={22} strokeWidth={1.75} />,
    },

    // 3. Users: Agents & Owners
    {
      path: "/super-admin/agents",
      label: "Agents / Builders",
      icon: <Users size={22} strokeWidth={1.75} />,
    },
    {
      path: "/super-admin/owners",
      label: "Owners",
      icon: <UserCog size={22} strokeWidth={1.75} />,
    },

    // 4. Leads: Properties → Interior
    {
      path: "/super-admin/leads/properties",
      label: "Property Leads",
      icon: <Building2 size={22} strokeWidth={1.75} />,
    },
    {
      path: "/super-admin/leads/interior",
      label: "Interior Leads",
      icon: <Armchair size={22} strokeWidth={1.75} />,
    },

    // 5. Advertisements
    {
      path: "/super-admin/advertisements",
      label: "Advertisements",
      icon: <Megaphone size={22} strokeWidth={1.75} />,
    },

    // 6. Profile & Subscription
    {
      path: "/super-admin/profile-subscriptions",
      label: "Profile & Subscription",
      icon: <BadgeCheck size={22} strokeWidth={1.75} />,
    },
  ];


  const isActive = (path) => {
    if (path === "/super-admin") {
      return location.pathname === "/super-admin";
    }
    return location.pathname.startsWith(path);
  };
  // const handleLogout = () => {
  //   localStorage.removeItem("adminToken");
  //   navigate("/login");
  // };

  // Close sidebar on screen resize (when growing to desktop size)
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 640 && isSidebarOpen && onClose) {
        onClose();
      }
      if (window.innerWidth <= 640) {
        setIsSidebarExpanded(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSidebarOpen, onClose, isSidebarExpanded]);

  // Close sidebar on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (event.target.closest('[data-hamburger="true"]')) {
        return;
      }

      if (
        isSidebarOpen &&
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target) &&
        onClose
      ) {
        onClose();
      }
    };

    if (isSidebarOpen) {
      const timeoutId = setTimeout(() => {
        document.addEventListener("mousedown", handleClickOutside, true);
        document.addEventListener("touchstart", handleClickOutside, true);
      }, 100);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener("mousedown", handleClickOutside, true);
        document.removeEventListener("touchstart", handleClickOutside, true);
      };
    }
  }, [isSidebarOpen, onClose]);

  return (
    <aside
      ref={sidebarRef}
      style={{ zIndex: Z_INDEX.sidebar }}
      className={`absolute top-0 left-0 bottom-0 pl-1.5 sm:pl-2 md:pl-3 text-[#61748F] bg-background h-full flex flex-col justify-between transition-all duration-300 ease-in-out 
      ${
        isOpen
          ? "w-60 shadow-sm shadow-[rgba(0,0,0,0.39)] ring-gray-200/40"
          : "w-[5.3rem] shadow-xl sm:flex -translate-x-full sm:translate-x-0"
      }`}
    >
      <div className="">
        {/* Collapse Toggle Button - Only on desktop */}
        <div className={`hidden sm:flex justify-start px-2 pt-3 pb-2 mr-4`}>
          <button
            onClick={() => setIsSidebarExpanded(!isSidebarExpanded)}
            className={`group flex items-center justify-center px-2 py-2 text-sm font-medium rounded-md transition-all duration-200 font-quicksand border-2 ${
              isSidebarExpanded
                ? "bg-primary/5 text-primary border-primary/20 hover:bg-primary/10 hover:border-primary/30 hover:shadow-[0_2px_8px_rgba(115,93,255,0.2)] "
                : "bg-primary/5 text-primary border-primary/20 hover:bg-primary/10 hover:border-primary/30 hover:shadow-[0_2px_8px_rgba(115,93,255,0.2)]"
            }`}
            title={isSidebarExpanded ? "Collapse sidebar" : "Expand sidebar"}
          >
            <div className="flex items-center space-x-1.5">
              {/* Hamburger/Toggle Icon */}
              <span className="text-xl flex-shrink-0 transition-transform duration-200 group-hover:scale-110">
                {isSidebarExpanded ? <RiMenuFoldLine /> : <RiMenuUnfoldLine />}
              </span>
            </div>
          </button>
        </div>

        {/* Menu Items */}
        <nav className="mt-2 space-y-2 px-2 font-quicksand">
          {menuItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`group flex items-center justify-between px-2 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                isActive(item.path)
                  ? "bg-primary text-white -translate-x-1 shadow-[0_4px_12px_rgba(115,93,255,0.4)] -translate-y-0.5"
                  : "text-gray-700 hover:bg-[#735dff85] hover:text-gray-800 hover:shadow-[0_2px_8px_rgba(115,93,255,0.2)] hover:translate-x-0.5"
              }`}
            >
              <div className="flex items-center space-x-1 md:space-x-1.5 overflow-hidden">
                <span className="text-xl flex-shrink-0">{item.icon}</span>
                <span
                  className={`whitespace-nowrap transition-all duration-300 ease-in-out ${
                    isOpen
                      ? "opacity-100 translate-x-0"
                      : "opacity-0 -translate-x-4 w-0"
                  }`}
                >
                  {item.label}
                </span>
              </div>
              <IoChevronForward
                size={18}
                className={`flex-shrink-0 transition-all duration-300 ease-in-out ${
                  isActive(item.path)
                    ? "opacity-100"
                    : isOpen
                    ? "opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5"
                    : "opacity-0 w-0"
                }`}
              />
            </Link>
          ))}
        </nav>
      </div>

      {/* Logout Button */}
      {/* <div
        className={`p-6 transition-all duration-300 ease-in-out ${
          !isOpen && "px-2"
        }`}
      >
        <button
          onClick={handleLogout}
          className={`w-full flex items-center justify-center font-semibold py-2.5 rounded-lg 
          bg-btn-brandRed text-white
          hover:bg-btn-warning hover:shadow-[0_4px_12px_rgba(255,90,41,0.3)] hover:-translate-y-0.5
          active:translate-y-0 transition-all duration-200 ${
            isOpen ? "space-x-2" : "space-x-0"
          }`}
        >
          <FiLogOut size={22} className="flex-shrink-0" />
          <span
            className={`whitespace-nowrap transition-all duration-300 ease-in-out ${
              isOpen
                ? "opacity-100 translate-x-0"
                : "opacity-0 -translate-x-4 w-0"
            }`}
          >
            Logout
          </span>
        </button>
      </div> */}
    </aside>
  );
};

export default SuperAdminSidebar;
// import { Link, useLocation, useNavigate } from "react-router-dom";
// import { FiHome, FiTrendingUp, FiUser, FiLogOut } from "react-icons/fi";
// import { IoChevronForward } from "react-icons/io5";
// import { RiUserSearchLine } from "react-icons/ri";
// import { PiBuildingApartmentThin } from "react-icons/pi";
// import { useEffect, useState, useRef } from "react";
// import { Z_INDEX } from "../utils/constants";

// const SuperAdminSidebar = ({ isSidebarOpen, onClose }) => {
//   const location = useLocation();
//   const navigate = useNavigate();
//   const [isExpanded, setIsExpanded] = useState(true);
//   const sidebarRef = useRef(null);

//   const isOpen = isExpanded || isSidebarOpen;

//   const menuItems = [
//     { path: "/admin", label: "Dashboard", icon: <FiHome size={22} /> },
//     {
//       path: "/admin/leads",
//       label: "Lead / Response",
//       icon: <RiUserSearchLine size={22} />,
//     },
//     {
//       path: "/admin/properties",
//       label: "My Properties",
//       icon: <PiBuildingApartmentThin strokeWidth={8} size={22} />,
//     },
//     {
//       path: "/admin/market-insights",
//       label: "Market Insights",
//       icon: <FiTrendingUp size={22} />,
//     },
//     {
//       path: "/admin/profile",
//       label: "Profile & Subscription",
//       icon: <FiUser size={22} />,
//     },
//   ];

//   const isActive = (path) => location.pathname === path;

//   const handleLogout = () => {
//     localStorage.removeItem("adminToken");
//     navigate("/admin/login");
//   };

//   // Close sidebar on screen resize (when growing to desktop size)
//   useEffect(() => {
//     const handleResize = () => {
//       if (window.innerWidth >= 640 && isSidebarOpen && onClose) {
//         onClose();
//       }
//     };

//     window.addEventListener("resize", handleResize);
//     return () => window.removeEventListener("resize", handleResize);
//   }, [isSidebarOpen, onClose]);

//   // Close sidebar on outside click
//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       // Ignore clicks on hamburger button
//       if (event.target.closest('[data-hamburger="true"]')) {
//         return;
//       }

//       // Check if sidebar is open and click is outside
//       if (
//         isSidebarOpen &&
//         sidebarRef.current &&
//         !sidebarRef.current.contains(event.target) &&
//         onClose
//       ) {
//         onClose();
//       }
//     };

//     // Add listeners with a small delay to avoid capturing the opening click
//     if (isSidebarOpen) {
//       const timeoutId = setTimeout(() => {
//         document.addEventListener("mousedown", handleClickOutside, true);
//         document.addEventListener("touchstart", handleClickOutside, true);
//       }, 100);

//       return () => {
//         clearTimeout(timeoutId);
//         document.removeEventListener("mousedown", handleClickOutside, true);
//         document.removeEventListener("touchstart", handleClickOutside, true);
//       };
//     }
//   }, [isSidebarOpen, onClose]);

//   return (
//     <aside
//       ref={sidebarRef}
//       style={{ zIndex: Z_INDEX.sidebar }}
//       className={`absolute top-0 left-0 bottom-0  pl-1.5 sm:pl-2 md:pl-3 text-[#61748F] bg-background h-full flex flex-col justify-between  transition-all duration-300 ease-in-out
//       ${
//         isOpen
//           ? "w-60 shadow-xl shadow-[rgba(0,0,0,0.39)]  ring-gray-200/40 "
//           : "w-[5.3rem] shadow-xl sm:flex -translate-x-full sm:translate-x-0"
//       }`}
//       // onMouseEnter={() => setIsExpanded(true)}
//       // onMouseLeave={() => !isSidebarOpen && setIsExpanded(false)}
//     >
//       <div>
//         {/* Menu Items */}
//         <nav className="mt-6 space-y-2 px-2 font-quicksand">

//           {menuItems.map((item) => (
//             <Link
//               key={item.path}
//               to={item.path}
//               className={`group flex items-center justify-between px-2 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
//                 isActive(item.path)
//                   ? "bg-primary text-white -translate-x-1 shadow-[0_4px_12px_rgba(115,93,255,0.4)] -translate-y-0.5 "
//                   : "text-gray-700 hover:bg-[#735dff85] hover:text-gray-800 hover:shadow-[0_2px_8px_rgba(115,93,255,0.2)] hover:translate-x-0.5"
//               }`}
//             >
//               <div className="flex items-center space-x-1 md:space-x-1.5 overflow-hidden">
//                 <span className="text-xl flex-shrink-0">{item.icon}</span>
//                 <span
//                   className={`whitespace-nowrap transition-all duration-300 ease-in-out ${
//                     isOpen
//                       ? "opacity-100 translate-x-0"
//                       : "opacity-0 -translate-x-4 w-0"
//                   }`}
//                 >
//                   {item.label}
//                 </span>
//               </div>
//               <IoChevronForward
//                 size={18}
//                 className={`flex-shrink-0 transition-all duration-300 ease-in-out ${
//                   isActive(item.path)
//                     ? "opacity-100"
//                     : isOpen
//                     ? "opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5"
//                     : "opacity-0 w-0"
//                 }`}
//               />
//             </Link>
//           ))}
//         </nav>
//       </div>

//       {/* Logout Button */}
//       <div
//         className={`p-6 transition-all duration-300 ease-in-out ${
//           !isOpen && "px-2"
//         }`}
//       >
//         <button
//           onClick={handleLogout}
//           className={`w-full flex items-center justify-center font-semibold py-2.5 rounded-lg
//           bg-btn-brandRed text-white
//           hover:bg-btn-warning hover:shadow-[0_4px_12px_rgba(255,90,41,0.3)] hover:-translate-y-0.5
//           active:translate-y-0 transition-all duration-200 ${
//             isOpen ? "space-x-2" : "space-x-0"
//           }`}
//         >
//           <FiLogOut size={22} className="flex-shrink-0" />
//           <span
//             className={`whitespace-nowrap transition-all duration-300 ease-in-out ${
//               isOpen
//                 ? "opacity-100 translate-x-0"
//                 : "opacity-0 -translate-x-4 w-0"
//             }`}
//           >
//             Logout
//           </span>
//         </button>
//       </div>
//     </aside>
//   );
// };

// export default SuperAdminSidebar;
